import { useMemo, useState } from "react";

function parseRecordDate(record) {
  const rawValue = record?.timestamp || "";
  const match = rawValue.match(
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:,\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );

  if (!match) {
    return null;
  }

  const [, day, month, year, hour = "0", minute = "0", second = "0"] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );
}

function isWithinDateRange(record, dateFrom, dateTo) {
  if (!dateFrom && !dateTo) {
    return true;
  }

  const recordDate = parseRecordDate(record);

  if (!recordDate) {
    return false;
  }

  if (dateFrom && recordDate < new Date(`${dateFrom}T00:00:00`)) {
    return false;
  }

  if (dateTo && recordDate > new Date(`${dateTo}T23:59:59`)) {
    return false;
  }

  return true;
}

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const restSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m ${String(restSeconds).padStart(2, "0")}s`;
}

function sumDurations(items) {
  return items.reduce(
    (sum, item) => sum + (Number(item.processDurationSeconds) || 0),
    0
  );
}

function buildStats(records, bioRecords, extraEfforts, suppliers) {
  const wareneingaenge = records.filter(
    (record) => record.type === "Wareneingang"
  );
  const retouren = records.filter((record) => record.type === "Retoure");
  const postruecklaeufer = records.filter(
    (record) => record.type === "Postrückläufer"
  );
  const totalWeSeconds = sumDurations(wareneingaenge);
  const averageWeSeconds =
    wareneingaenge.length > 0
      ? Math.round(totalWeSeconds / wareneingaenge.length)
      : 0;
  const suppliersWithActivity = new Set([
    ...records.map((record) => record.selectedSupplier),
    ...bioRecords.map((record) => record.selectedSupplier),
    ...extraEfforts.map((effort) => effort.customer),
  ]).size;

  return [
    { label: "Gesamtvorgaenge", value: records.length + bioRecords.length + extraEfforts.length, tone: "dark" },
    { label: "Wareneingaenge", value: wareneingaenge.length, tone: "green" },
    { label: "Retouren", value: retouren.length, tone: "red" },
    { label: "Postruecklaeufer", value: postruecklaeufer.length, tone: "gold" },
    {
      label: "Aktive Kunden",
      value: `${suppliersWithActivity} / ${suppliers.length}`,
      tone: "gold",
    },
    { label: "Durchschnitt WE", value: formatDuration(averageWeSeconds), tone: "dark", isTime: true },
    { label: "Zeit WE", value: formatDuration(totalWeSeconds), tone: "green", isTime: true },
    { label: "Zeit Retoure", value: formatDuration(sumDurations(retouren)), tone: "red", isTime: true },
    { label: "Zeit Postruecklaeufer", value: formatDuration(sumDurations(postruecklaeufer)), tone: "gold", isTime: true },
    { label: "Zeit BIO WE", value: formatDuration(sumDurations(bioRecords)), tone: "gold", isTime: true },
    { label: "Zeit Mehraufwand", value: formatDuration(sumDurations(extraEfforts)), tone: "dark", isTime: true },
  ];
}

function downloadCsv(fileName, rows) {
  const csvContent = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(";")
    )
    .join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function Dashboard({
  records,
  outgoingRecords = [],
  bioRecords = [],
  extraEfforts = [],
  suppliers,
  user,
  backups = [],
  onCreateBackup,
}) {
  const [exportSupplier, setExportSupplier] = useState("all");
  const [exportType, setExportType] = useState("regular");
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("tag");
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);

  const latestRecords = useMemo(() => [...records].reverse().slice(0, 5), [records]);
  const suppliersByName = useMemo(() => {
    return new Map(suppliers.map((supplier) => [supplier.name, supplier]));
  }, [suppliers]);
  const latestRecordsWithSuppliers = useMemo(
    () =>
      latestRecords.map((record) => ({
        ...record,
        supplier: suppliersByName.get(record.selectedSupplier),
      })),
    [latestRecords, suppliersByName]
  );

  const mainStats = useMemo(
    () => buildStats(records, bioRecords, extraEfforts, suppliers),
    [bioRecords, extraEfforts, records, suppliers]
  );

  const filteredRecords = useMemo(
    () =>
      records.filter((record) =>
        isWithinDateRange(record, filterDateFrom, filterDateTo)
      ),
    [filterDateFrom, filterDateTo, records]
  );

  const filteredBioRecords = useMemo(
    () =>
      bioRecords.filter((record) =>
        isWithinDateRange(record, filterDateFrom, filterDateTo)
      ),
    [bioRecords, filterDateFrom, filterDateTo]
  );

  const filteredExtraEfforts = useMemo(
    () =>
      extraEfforts.filter((effort) =>
        isWithinDateRange(effort, filterDateFrom, filterDateTo)
      ),
    [extraEfforts, filterDateFrom, filterDateTo]
  );

  const filteredStats = useMemo(
    () =>
      buildStats(
        filteredRecords,
        filteredBioRecords,
        filteredExtraEfforts,
        suppliers
      ),
    [filteredBioRecords, filteredExtraEfforts, filteredRecords, suppliers]
  );

  const exportableRows = useMemo(() => {
    const matchesSupplierName = (name) =>
      exportSupplier === "all" || name === exportSupplier;
    const matchesExportDate = (item) =>
      isWithinDateRange(item, exportDateFrom, exportDateTo);
    const getCustomerNumber = (name) =>
      suppliersByName.get(name)?.customerNumber || "";
    const getBioLabel = (supplier) => {
      if (supplier?.bioMode === "bio_und_nicht_bio") {
        return "Bio und nicht bio";
      }

      return supplier?.bio ? "Ja" : "Nein";
    };

    const regularRows = [...records]
      .reverse()
      .filter((record) => {
        const matchesType =
          exportType === "regular" ||
          exportType === "complete" ||
          record.type === exportType;

        return (
          matchesType &&
          matchesSupplierName(record.selectedSupplier) &&
          matchesExportDate(record)
        );
      })
      .map((record) => {
        const supplier = suppliersByName.get(record.selectedSupplier);

        return [
          "Wareneingang/Retoure/Postrückläufer",
          record.timestamp,
          record.type,
          record.selectedSupplier,
          getCustomerNumber(record.selectedSupplier),
          record.packageCount,
          record.packageCondition,
          record.palletCount,
          record.palletType,
          record.palletCondition,
          Number(record.damagedPackages) || 0,
          Number(record.damagedPallets) || 0,
          "",
          "",
          "",
          "",
          "",
          "",
          getBioLabel(supplier),
          supplier?.tracesImport ? "Ja" : "Nein",
          record.comment,
          formatDuration(record.processDurationSeconds),
          record.processed ? "Ja" : "Nein",
          "",
        ];
      });

    const outgoingRows = [...outgoingRecords]
      .reverse()
      .filter(
        (record) =>
          (exportType === "warenausgang" || exportType === "complete") &&
          matchesSupplierName(record.selectedSupplier) &&
          matchesExportDate(record)
      )
      .map((record) => {
        const supplier = suppliersByName.get(record.selectedSupplier);

        return [
          "Warenausgang",
          record.timestamp,
          "Warenausgang",
          record.selectedSupplier,
          getCustomerNumber(record.selectedSupplier),
          record.packageCount,
          record.packageCondition,
          record.palletCount,
          record.palletType,
          record.palletCondition,
          Number(record.damagedPackages) || 0,
          Number(record.damagedPallets) || 0,
          "",
          "",
          "",
          "",
          "",
          "",
          getBioLabel(supplier),
          supplier?.tracesImport ? "Ja" : "Nein",
          record.comment,
          formatDuration(record.processDurationSeconds),
          record.processed ? "Ja" : "Nein",
          Array.isArray(record.abdDataList) && record.abdDataList.length > 0
            ? `ABD vorhanden (${record.abdDataList.length})`
            : "",
        ];
      });

    const bioRows = [...bioRecords]
      .reverse()
      .filter(
        (record) =>
          (exportType === "bio" || exportType === "complete") &&
          matchesSupplierName(record.selectedSupplier) &&
          matchesExportDate(record)
      )
      .map((record) => {
        const blocked = (record.checklistData || []).some(
          (item) => item.answer === "nein"
        );
        const auditText = (record.checklistData || [])
          .map(
            (item) =>
              `${item.answer === "ja" ? "Ja" : "Nein"}: ${item.question}${
                item.reason ? ` (${item.reason})` : ""
              }`
          )
          .join(" | ");

        return [
          "BIO WE",
          record.timestamp,
          "BIO WE",
          record.selectedSupplier,
          getCustomerNumber(record.selectedSupplier),
          record.packageCount,
          record.packageCondition,
          record.palletCount,
          record.palletType,
          record.palletCondition,
          0,
          0,
          "",
          "",
          "",
          "",
          "",
          "",
          "Ja",
          "",
          record.comment,
          formatDuration(record.processDurationSeconds),
          record.processed ? "Ja" : "Nein",
          `${blocked ? "Gesperrt" : "Freigegeben"}${auditText ? ` - ${auditText}` : ""}`,
        ];
      });

    const extraRows = [...extraEfforts]
      .reverse()
      .filter(
        (effort) =>
          (exportType === "extra" || exportType === "complete") &&
          matchesSupplierName(effort.customer) &&
          matchesExportDate(effort)
      )
      .map((effort) => [
        "Mehraufwand",
        effort.timestamp,
        "Mehraufwand",
        effort.customer,
        getCustomerNumber(effort.customer),
        "",
        "",
        "",
        "",
        "",
        Number(effort.damagedPackages) || 0,
        Number(effort.damagedPallets) || 0,
        effort.effortType === "VAS sonstiges" && effort.customEffort
          ? `${effort.effortType}: ${effort.customEffort}`
          : effort.effortType,
        effort.effortType === "Auslagen" ? "Ja" : "Nein",
        effort.expenseType || "",
        Number(effort.expenseAmount) || 0,
        effort.effortType === "Auslagen" ? "" : effort.durationMinutes,
        effort.employee,
        "",
        "",
        effort.comment,
        formatDuration(effort.processDurationSeconds),
        effort.processed ? "Ja" : "Nein",
        effort.photoData ? "Foto vorhanden" : "",
      ]);

    if (exportType === "bio") {
      return bioRows;
    }

    if (exportType === "warenausgang") {
      return outgoingRows;
    }

    if (exportType === "extra") {
      return extraRows;
    }

    if (exportType === "complete") {
      return [...regularRows, ...outgoingRows, ...bioRows, ...extraRows];
    }

    return regularRows;
  }, [
    bioRecords,
    exportDateFrom,
    exportDateTo,
    exportSupplier,
    exportType,
    extraEfforts,
    outgoingRecords,
    records,
    suppliersByName,
  ]);

  const handleCsvDownload = () => {
    if (exportableRows.length === 0) {
      window.alert("Es gibt keine Daten fuer die aktuellen Export-Filter.");
      return;
    }

    downloadCsv(`export-${exportType}-${exportDateFrom || "start"}-${exportDateTo || "ende"}.csv`, [
      [
        "Bereich",
        "Zeit",
        "Typ",
        "Kunde",
        "Kundennummer",
        "Pakete",
        "Zustand Pakete",
        "Paletten",
        "Palettentyp",
        "Zustand Paletten",
        "Beschädigte Pakete",
        "Beschädigte Paletten",
        "Aufwand",
        "Auslagen",
        "Auslagenart",
        "Wert EUR",
        "Zeit in Minuten",
        "Mitarbeiter",
        "Bio",
        "Traces",
        "Kommentar",
        "Bearbeitungszeit",
        "Bearbeitet",
        "Status/Pruefung",
      ],
      ...exportableRows,
    ]);
  };

  const handleBackupCreate = async () => {
    try {
      setIsCreatingBackup(true);
      const backup = await onCreateBackup();
      window.alert(`Backup erstellt: ${backup.fileName}`);
    } catch (error) {
      window.alert(error.message);
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const latestBackup = backups[0];

  return (
    <div className="page-stack dashboard-page">
      <div>
        <p className="eyebrow">Dashboard</p>
        <h1 className="page-title">Ueberblick ueber dein Lager</h1>
        <p className="section-text">
          Dashboard ist nur fuer Admins am PC sichtbar.
        </p>
      </div>

      <StatsRow stats={mainStats} />

      <section className="card dashboard-filter-card">
        <div className="section-head">
          <div>
            <h2>Zeitraum-Auswertung</h2>
            <p className="section-text">
              Waehle Zeitraum und Ansicht fuer die Dashboardwerte.
            </p>
          </div>
        </div>

        <div className="form-grid columns-3">
          <label className="field">
            <span>Datum von</span>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(event) => setFilterDateFrom(event.target.value)}
            />
          </label>

          <label className="field">
            <span>Datum bis</span>
            <input
              type="date"
              value={filterDateTo}
              onChange={(event) => setFilterDateTo(event.target.value)}
            />
          </label>

          <label className="field">
            <span>Auswertung</span>
            <select
              value={filterPeriod}
              onChange={(event) => setFilterPeriod(event.target.value)}
            >
              <option value="tag">Pro Tag</option>
              <option value="woche">Pro Woche</option>
              <option value="monat">Pro Monat</option>
              <option value="jahr">Pro Jahr</option>
            </select>
          </label>
        </div>

        <p className="section-text dashboard-filter-label">
          Aktuelle Ansicht: {filterPeriod}
        </p>

        <StatsRow stats={filteredStats} />
      </section>

      {user?.isAdmin && (
        <>
          <section className="card">
            <div className="section-head">
              <div>
                <h2>CSV-Download</h2>
                <p className="section-text">
                  Exportiere alle Daten oder nur gefilterte Daten als CSV-Datei.
                </p>
              </div>
              <button
                onClick={handleCsvDownload}
                className="button button-primary"
              >
                CSV herunterladen
              </button>
            </div>

            <div className="form-grid columns-2">
              <label className="field">
                <span>Kunde fuer Export</span>
                <select
                  value={exportSupplier}
                  onChange={(event) => setExportSupplier(event.target.value)}
                >
                  <option value="all">Alle Kunden</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.name}>
                      {supplier.customerNumber
                        ? `${supplier.short} - ${supplier.customerNumber}`
                        : supplier.short}
                    </option>
                  ))}
                </select>
              </label>

              <div className="field">
                <span>Vorgangstyp fuer Export</span>
                <div className="filter-button-row">
                  {[
                    ["regular", "WE + Retoure + Postrückläufer"],
                    ["Wareneingang", "Wareneingang"],
                    ["Retoure", "Retoure"],
                    ["Postrückläufer", "Postrückläufer"],
                    ["warenausgang", "Warenausgang"],
                    ["bio", "BIO WE"],
                    ["extra", "Mehraufwand"],
                    ["complete", "Komplett"],
                  ].map(([type, label]) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setExportType(type)}
                      className={`button ${
                        exportType === type ? "button-primary" : "button-secondary"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="field">
                <span>Datum von</span>
                <input
                  type="date"
                  value={exportDateFrom}
                  onChange={(event) => setExportDateFrom(event.target.value)}
                />
              </label>

              <label className="field">
                <span>Datum bis</span>
                <input
                  type="date"
                  value={exportDateTo}
                  onChange={(event) => setExportDateTo(event.target.value)}
                />
              </label>
            </div>
          </section>

          <section className="card">
            <div className="section-head">
              <div>
                <h2>Backups</h2>
                <p className="section-text">
                  Erstelle Sicherungen deiner Datenbank. Angezeigt wird nur das letzte Backup.
                </p>
              </div>
              <button
                onClick={handleBackupCreate}
                className="button button-dark"
                disabled={isCreatingBackup}
              >
                {isCreatingBackup ? "Backup wird erstellt..." : "Backup erstellen"}
              </button>
            </div>

            {!latestBackup ? (
              <div className="empty-state">Es wurde noch kein Backup erstellt.</div>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Datei</th>
                      <th>Erstellt am</th>
                      <th>Groesse</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr key={latestBackup.fileName}>
                      <td>{latestBackup.fileName}</td>
                      <td>{latestBackup.createdAt}</td>
                      <td>{latestBackup.sizeKb}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      <section className="card">
        <h2>Letzte Vorgaenge</h2>
        <p className="section-text">
          Die zuletzt gespeicherten Eintraege werden hier angezeigt.
        </p>

        {latestRecords.length === 0 ? (
          <div className="empty-state">
            Noch keine Daten vorhanden.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table dashboard-latest-table">
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Typ</th>
                  <th>Kunde</th>
                  <th>Kundennummer</th>
                  <th>Pakete</th>
                  <th>Paletten</th>
                  <th>Beschädigte Pakete</th>
                  <th>Beschädigte Paletten</th>
                  <th>Bearbeitungszeit</th>
                  <th>Bearbeitet</th>
                </tr>
              </thead>
              <tbody>
                {latestRecordsWithSuppliers.map((record) => (
                  <tr
                    key={record.id}
                    className={record.processed ? "row-processed" : ""}
                  >
                    <td>{record.timestamp}</td>
                    <td>{record.type}</td>
                    <td>{record.selectedSupplier}</td>
                    <td>{record.supplier?.customerNumber || "-"}</td>
                    <td>{record.packageCount}</td>
                      <td>{record.palletCount}</td>
                      <td>{Number(record.damagedPackages) || 0}</td>
                      <td>{Number(record.damagedPallets) || 0}</td>
                      <td>{formatDuration(record.processDurationSeconds)}</td>
                    <td>{record.processed ? "Ja" : "Nein"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatsRow({ stats }) {
  return (
    <div className="stats-grid dashboard-stats-row">
      {stats.map((stat) => (
        <StatCard
          key={stat.label}
          label={stat.label}
          value={stat.value}
          tone={stat.tone}
          isTime={stat.isTime}
        />
      ))}
    </div>
  );
}

function StatCard({ label, value, tone, isTime = false }) {
  return (
    <div className={`stat-card stat-card-${tone} ${isTime ? "stat-card-time" : ""}`}>
      <div className="stat-label">
        {label}
      </div>
      <div className="stat-value">{value}</div>
    </div>
  );
}


